import { useCallback, useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { ArrowRight, List } from '@phosphor-icons/react';
import { SECTION_IDS, site } from '../../content/site';
import { useLenis } from '../../lib/SmoothScroll';
import { scrollToId } from '../../lib/scroll';
import { EASE, MOTION_OK, ScrollTrigger, gsap, useGSAP } from '../../lib/gsap';
import { MobileMenu } from '../nav/MobileMenu';
import { ThemeToggle } from '../nav/ThemeToggle';
import { useActiveSection } from '../nav/useActiveSection';

const LINK_IDS = site.nav.links.map((link) => link.id);
const MENU_ID = 'menu-mobile';
const DESKTOP_NAV = '(min-width: 1024px)';
/** Above this scroll position (px) the pill never hides. */
const ALWAYS_VISIBLE_UNTIL = 160;
/** Downward scroll speed (px/s) from which the pill slides away. */
const HIDE_VELOCITY = 450;
/** Above this speed (px/s) a scroll is a jump (hash on load, immediate scrollTo), not a reader. */
const JUMP_VELOCITY = 20000;
/** How long the pill stays put after a nav link starts a smooth scroll (Lenis takes 1.4s). */
const HOLD_AFTER_NAV_MS = 1800;

/**
 * Glass pill: Mineral frost by day, Forest Night frost at dusk and in the dark theme. Every
 * color is a --nav-* token, switched by html[data-chapter="dusk"] and html[data-theme="dark"], so the pill only has to
 * transition. The inset top highlight gives the glass its edge.
 */
const PILL = [
	'pointer-events-auto mx-auto grid h-14 max-w-[1180px] grid-cols-[1fr_auto] items-center rounded-full',
	'border border-[color:var(--nav-border)] bg-[color:var(--nav-bg)] text-[color:var(--nav-ink)]',
	'pl-5 pr-1.5 backdrop-blur-xl backdrop-saturate-150',
	'shadow-[inset_0_1px_0_rgba(247,247,240,0.55),0_14px_40px_-26px_rgba(41,77,59,0.45)]',
	'[[data-chapter=dusk]_&]:shadow-[inset_0_1px_0_rgba(247,247,240,0.06),0_14px_40px_-26px_rgba(15,29,22,0.8)]',
	'dark:shadow-[inset_0_1px_0_rgba(247,247,240,0.06),0_14px_40px_-26px_rgba(15,29,22,0.8)]',
	'[@media(prefers-reduced-transparency:reduce)]:bg-[color:var(--page-bg)]',
	'transition-[background-color,border-color,color,box-shadow] duration-700 ease-expo',
	'lg:h-16 lg:grid-cols-[1fr_auto_1fr] lg:pl-7 lg:pr-2.5',
].join(' ');

/**
 * Floating navigation. Desktop: wordmark, the four chapters (the one in view is
 * marked with aria-current) and the single contact CTA. Below 1024px: wordmark and
 * a menu button opening a full-screen sheet. With motion allowed, the pill slides
 * away on a fast downward scroll and comes back on any upward scroll.
 */
export function Nav() {
	const lenis = useLenis();
	const activeId = useActiveSection(LINK_IDS);
	const [menuOpen, setMenuOpen] = useState(false);
	const headerRef = useRef<HTMLElement>(null);
	const pillRef = useRef<HTMLElement>(null);
	const menuButtonRef = useRef<HTMLButtonElement>(null);
	const menuOpenRef = useRef(false);
	const pendingTarget = useRef<string | null>(null);
	const holdVisibleUntil = useRef(0);

	useEffect(() => {
		menuOpenRef.current = menuOpen;
	}, [menuOpen]);

	// Navigating with the nav must not make it slide away on the way down.
	const holdVisible = useCallback(() => {
		holdVisibleUntil.current = performance.now() + HOLD_AFTER_NAV_MS;
	}, []);

	const go = (id: string) => (event: MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		holdVisible();
		scrollToId(lenis, id);
	};

	const closeMenu = useCallback(() => {
		setMenuOpen(false);
		menuButtonRef.current?.focus({ preventScroll: true });
	}, []);

	const navigateFromMenu = useCallback(
		(id: string) => {
			holdVisible();
			pendingTarget.current = id;
			closeMenu();
		},
		[closeMenu, holdVisible],
	);

	// Freeze the page behind the sheet (Lenis for the wheel, overflow for touch and no-Lenis).
	useEffect(() => {
		if (!menuOpen) return;
		const root = document.documentElement;
		const previous = root.style.overflow;
		root.style.overflow = 'hidden';
		lenis?.stop();
		return () => {
			root.style.overflow = previous;
			lenis?.start();
		};
	}, [menuOpen, lenis]);

	// A link of the sheet was chosen: scroll once the lock above has been released.
	useEffect(() => {
		if (menuOpen || !pendingTarget.current) return;
		const id = pendingTarget.current;
		pendingTarget.current = null;
		scrollToId(lenis, id);
	}, [menuOpen, lenis]);

	// The sheet only exists below 1024px: never leave it open (and the page locked) on resize.
	useEffect(() => {
		const query = window.matchMedia(DESKTOP_NAV);
		const onChange = () => {
			if (query.matches) setMenuOpen(false);
		};
		query.addEventListener('change', onChange);
		return () => query.removeEventListener('change', onChange);
	}, []);

	useGSAP(
		() => {
			const pill = pillRef.current;
			if (!pill) return;
			const mm = gsap.matchMedia();

			mm.add(MOTION_OK, () => {
				// Arrives just after the headline starts, so the title leads.
				gsap.from(pill, { y: -20, autoAlpha: 0, duration: 1.2, delay: 0.35 });

				let hidden = false;
				const setHidden = (next: boolean) => {
					if (next === hidden) return;
					hidden = next;
					gsap.to(pill, {
						yPercent: next ? -150 : 0,
						duration: next ? 0.5 : 0.8,
						ease: next ? 'power2.in' : EASE,
						overwrite: 'auto',
					});
				};

				ScrollTrigger.create({
					start: 0,
					end: 'max',
					onUpdate: (self) => {
						const keep =
							self.scroll() < ALWAYS_VISIBLE_UNTIL ||
							menuOpenRef.current ||
							performance.now() < holdVisibleUntil.current ||
							pill.matches(':focus-within, :hover');
						if (keep || self.direction < 0) {
							setHidden(false);
							return;
						}
						const velocity = self.getVelocity();
						if (velocity > HIDE_VELOCITY && velocity < JUMP_VELOCITY) setHidden(true);
					},
				});

				// Keyboard users tabbing into a hidden pill get it back immediately.
				const reveal = () => setHidden(false);
				pill.addEventListener('focusin', reveal);
				return () => pill.removeEventListener('focusin', reveal);
			});
		},
		{ scope: headerRef },
	);

	return (
		<>
			<header ref={headerRef} className="pointer-events-none fixed inset-x-0 top-0 z-40 px-3 pt-3 sm:px-4 lg:pt-4">
				<nav ref={pillRef} aria-label={site.nav.ariaLabel} className={PILL}>
					<a
						href={`#${SECTION_IDS.hero}`}
						onClick={go(SECTION_IDS.hero)}
						className="inline-flex h-11 items-center justify-self-start font-display text-[1.1875rem] font-bold tracking-[-0.02em] transition-opacity duration-300 hover:opacity-75"
					>
						{site.person.name}
					</a>

					<ul className="hidden items-center gap-0.5 lg:flex">
						{site.nav.links.map((link) => {
							const current = activeId === link.id;
							return (
								<li key={link.id}>
									<a
										href={`#${link.id}`}
										onClick={go(link.id)}
										aria-current={current ? 'true' : undefined}
										className={`group relative inline-flex h-11 items-center rounded-full px-4 text-[0.9375rem] font-medium tracking-[-0.005em] transition-colors duration-300 ${
											current
												? 'text-[color:var(--nav-ink)]'
												: 'text-[color:var(--nav-ink-muted)] hover:text-[color:var(--nav-ink)]'
										}`}
									>
										<span
											aria-hidden
											className={`absolute inset-0 rounded-full bg-current transition-opacity duration-500 ease-expo ${
												current ? 'opacity-[0.07]' : 'opacity-0 group-hover:opacity-[0.04]'
											}`}
										/>
										<span className="relative">{link.label}</span>
									</a>
								</li>
							);
						})}
					</ul>

					<div className="flex items-center justify-self-end lg:gap-1.5">
						<ThemeToggle className="hidden lg:grid" />
						<a
							href={`#${SECTION_IDS.contact}`}
							onClick={go(SECTION_IDS.contact)}
							className="group hidden h-11 items-center gap-2 rounded-full bg-[color:var(--nav-cta-bg)] pl-5 pr-4 text-[0.9375rem] font-medium tracking-[-0.01em] text-[color:var(--nav-cta-ink)] transition-[background-color,color,transform] duration-300 ease-expo hover:bg-[color:var(--nav-cta-bg-hover)] active:scale-[0.98] lg:inline-flex"
						>
							{site.nav.cta}
							<ArrowRight
								aria-hidden
								size={16}
								weight="bold"
								className="transition-transform duration-300 ease-expo group-hover:translate-x-0.5"
							/>
						</a>
						<button
							ref={menuButtonRef}
							type="button"
							onClick={() => setMenuOpen(true)}
							aria-expanded={menuOpen}
							aria-controls={MENU_ID}
							aria-label={site.nav.menuOpen}
							className="grid h-11 w-11 place-items-center rounded-full transition-colors duration-300 hover:bg-[color:var(--nav-border)] lg:hidden"
						>
							<List aria-hidden size={22} />
						</button>
					</div>
				</nav>
			</header>

			<MobileMenu
				id={MENU_ID}
				open={menuOpen}
				activeId={activeId}
				onClose={closeMenu}
				onNavigate={navigateFromMenu}
			/>
		</>
	);
}
